import React from 'react';
import { useParams } from "react-router-dom";
import { Notification, NotificationGroup, } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";

import Api from '../../services/api/api';
import EstimateDatePanel from './EstimateDatePanel';
import PatientInfoPanel from './PatientInfoPanel';
import DeductiblePanel from './DeductiblePanel';
import CoinsurancePanel from './CoinsurancePanel';
import CopayPanel from './CopayPanel';
import MaxOutOfPocketPanel from './MaxOutOfPocketPanel';
import CodeTablePanel from './CodeTablePanel';
import EstimateTotalsPanel from './EstimateTotalsPanel';
import Disclaimer from '../shared/Disclaimer';

class Worksheet extends React.Component {

    constructor(props) {
        super(props);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        this.maxCodes = props.maxCodes ?? 30;

        const useDefaults = String(process.env.REACT_APP_USE_DEFAULTS).toLowerCase() === 'true';

        this.defaultProvider = useDefaults ? {
            name: 'DOCTOR TEST, MD',
            type: 'MD',
            isPcp: false
        } : null;

        this.defaultInsurance = useDefaults ? {
            name: 'TEST BCBS',
            type: 'BCBS-FL',
            isSelfPay: false,
            isBound: true
        } : null;

        this.defaultLocation = useDefaults ? {
            departmentId: 5186,
            name: 'TEST FACILITY',
            isFacility: false,
            isTrauma: false
        } : null;

        this.defaultCodeList = useDefaults ? [{
            code: '99205',
            name: 'NEW PATIENT OFFICE/OUTPATIENT VISIT...',
            wrvu: 3.17,
            chargeAmount: 447,
            altAmount: 447,
            adminCodes: null,
            comment: null,
            parentId: null,
            units: 1
        }] : []

        this.alteredState = {
            altered: true,
            estimateDate: today
        };

        this.defaultState = {
            id: null,
            estimateDate: today,
            dateOfService: today,
            deductible: 0,
            paidIn: 0,
            deductibleOwed: 0,
            coinsurance: 0,
            copay: 0,
            maxOutOfPocket: 0,
            maxOutOfPocketPaidIn: 0,
            maxOutOfPocketRemaining: 0,
            codeList: this.defaultCodeList,
            selectedPatient: null,
            selectedProvider: this.defaultProvider,
            selectedInsurance: this.defaultInsurance,
            selectedLocation: this.defaultLocation,
            altered: false
        };

        this.state = Object.assign({}, this.defaultState);

        this.handleDateOfServiceChange = this.handleDateOfServiceChange.bind(this);
        this.handlePatientAdd = this.handlePatientAdd.bind(this);
        this.handlePatientChange = this.handlePatientChange.bind(this);
        this.handleProviderChange = this.handleProviderChange.bind(this);
        this.handleInsuranceChange = this.handleInsuranceChange.bind(this);
        this.handleLocationChange = this.handleLocationChange.bind(this);
        this.handleDeductibleChange = this.handleDeductibleChange.bind(this);
        this.handlePaidInChange = this.handlePaidInChange.bind(this);
        this.handleDeductibleOwedChange = this.handleDeductibleOwedChange.bind(this);
        this.handleCoinsuranceChange = this.handleCoinsuranceChange.bind(this);
        this.handleCopayChange = this.handleCopayChange.bind(this);
        this.handleMaxOutOfPocketChange = this.handleMaxOutOfPocketChange.bind(this);
        this.handleMaxOutOfPocketPaidInChange = this.handleMaxOutOfPocketPaidInChange.bind(this);
        this.handleMaxOutOfPocketRemainingChange = this.handleMaxOutOfPocketRemainingChange.bind(this);
        this.handleCodeUnitsChange = this.handleCodeUnitsChange.bind(this);
        this.handleCodeDelete = this.handleCodeDelete.bind(this);
        this.handleCodeAdd = this.handleCodeAdd.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.resolvePatient = this.resolvePatient.bind(this);
        this.resolveCodes = this.resolveCodes.bind(this);
        this.toast = this.toast.bind(this);
        this.saveData = this.saveData.bind(this);
    }

    componentDidMount() {
        const params = this.props.params;

        // Initialize state either from estimateId, patientId, or default state
        if (params.estimateId) {
            this.initializeStateByEstimateId(params.estimateId);
        } else if (params.patientId) {
            this.initializeStateByPatientId(params.patientId);
        } else {
            this.initializeState();
        }
    }

    initializeState() {
        let state = Object.assign({}, this.defaultState);

        this.setState(state);
    }

    initializeStateByEstimateId(estimateId) {
        let state = Object.assign({}, this.defaultState);

        this.setState(state, () => {
            Api.getSummary(estimateId)
                .then((result) => {
                    if (result) {
                        console.debug('Loaded state...');
                        console.debug(JSON.stringify(result));

                        if (result.id) {
                            result.estimateDate = new Date(result.estimateDate);
                            result.dateOfService = new Date(result.dateOfService);
                            //result.selectedPatient.dateOfBirth = new Date(result.dateOfBirth);

                            this.setState(result);
                        }
                    }
                });
        });
    }

    initializeStateByPatientId(patientId) {
        let state = Object.assign({}, this.defaultState);

        this.setState(state, () => {
            this.resolvePatient(patientId);

            Api.getPatient(patientId)
                .then((result) => {
                    if (result && result.length) {
                        console.debug('Loaded patient...');
                        console.debug(JSON.stringify(result[0]));
                    }
                });
        });
    }

    resolvePatient(patientId) {
        Api.getPatient(patientId)
            .then((result) => {
                if (result.length) {
                    const patient = result[0]; // First result
                    const currentInsurance = this.state.selectedInsurance;

                    // Update patient info and insurance
                    this.setState({
                        selectedPatient: patient,
                        ...this.alteredState
                    });

                    // If insurance has been updated, send to handler
                    if (currentInsurance?.type !== patient?.insType) {
                        this.handleInsuranceChange(
                            {
                                name: patient.insName,
                                type: patient.insType,
                                isSelfPay: patient.insIsSelfPay
                            }
                        );
                    }
                }
            });
    }

    resolveCodes(insurance, location, provider) {
        const newList = []; // Build new cpt code list for state change
        const jobs = []; // Keep track of Promises

        this.state.codeList.forEach((code, index) => {
            const codeInfo = code; // make a copy of each code. These will get pushed back into the new list.

            jobs.push(Api.getCode(codeInfo.code, insurance, location, provider)
                .then((result) => {
                    // After each job returns, adjust the ChargeAmount and AltAmount code values
                    if (result) {
                        codeInfo.ChargeAmount = result.ChargeAmount;
                        codeInfo.AltAmount = result.AltAmount;
                    }

                    // Add to the new list.
                    newList[index] = codeInfo;
                }));
        });

        // When all Promises conclude, set our codeList state to the new list.
        Promise.all(jobs).then(() => {
            this.setState({
                codeList: newList,
                ...this.alteredState
            });
        });
    }

    addCode(codeData, insurance, location, provider) {
        const codeValue = codeData.code;

        // Don't add the same code twice (unless being added as an Admin Code)
        if (this.state.codeList.find((item) => item.code === codeValue)) {
            this.toast("Invalid Action", `${codeValue} is already in the list.`, 'warning');
            return;
        }

        // Check max rows
        if (this.state.codeList.length >= this.maxCodes) {
            this.toast("Invalid Action", `Cannot exceed ${this.maxCodes} rows.`, 'warning');
            return;
        }

        // Create lookup array for code resolution
        let codes = [codeValue];

        // Process admin codes
        if (codeData.adminCodes) {
            const adminCodes = codeData.adminCodes.split(',');
            codes = [...codes, ...adminCodes];
        }

        // Create array of lookup Promise objects
        const lookups = codes.map(code => Api.getCode(code, insurance, location, provider));

        Promise.all(lookups).then(results => {
            results.forEach((result, i) => {
                // Give a default of 1 unit
                result.units = 1;
                if (i !== 0) {
                    result.parent = results[0];
                }
            })

            //console.log(JSON.stringify(results));

            // Insert to our codeList
            this.setState({
                codeList: [...this.state.codeList, ...results],
                ...this.alteredState
            });
        });
    }

    handleDateOfServiceChange(value) {
        if (value >= new Date(2014, 1, 1) && value <= new Date(2100, 1, 1)) {
            this.setState({
                dateOfService: value,
                ...this.alteredState
            });
        }
    }

    handlePatientAdd(patientData) {

        if (!patientData) {
            this.setState({
                selectedPatient: null,
                ...this.alteredState
            });
        } else {
            this.setState({
                selectedPatient: patientData,
                ...this.alteredState
            });
        }
    }

    handlePatientChange(value) {
        if (!value) {
            this.setState({
                selectedPatient: null,
                ...this.alteredState
            });
        }
        else if (!this.state.selectedPatient || value.patientId !== this.state.selectedPatient.patientId) {
            const patient = value;
            this.setState({
                selectedPatient: patient,
                ...this.alteredState
            });
            this.resolvePatient(patient.patientId);
        }
    }

    handleProviderChange(value) {
        let oldProviderType = this.state.selectedProvider?.type;
        let newProviderType = value?.type;

        // if the provider changes provider types, we need to refresh our code list.
        if (oldProviderType !== newProviderType) {
            this.resolveCodes(this.state.selectedInsurance, this.state.selectedLocation, value);
        }

        this.setState({
            selectedProvider: value,
            ...this.alteredState
        });
    }

    handleInsuranceChange(value) {

        // if we transition to Self Pay, we must zero out the deductible, co-insurance, and copay fields.
        if (!this.state.selectedInsurance?.isSelfPay && value?.isSelfPay) {
            this.setState({
                deductible: 0,
                paidIn: 0,
                coinsurance: 0,
                copay: 0
            });
        }

        // if the insurance type changes, we need to refresh our code list.
        if (value?.type !== this.state.selectedInsurance?.type) {
            this.resolveCodes(value, this.state.selectedLocation, this.state.selectedProvider);
        }

        this.setState({
            selectedInsurance: value,
            ...this.alteredState
        });
    }

    handleLocationChange(value) {

        // Locations are all unique by departmentId, so we must always refresh our code list.
        this.resolveCodes(this.state.selectedInsurance, value, this.state.selectedProvider);

        this.setState({
            selectedLocation: value,
            ...this.alteredState
        });
    }

    handleDeductibleChange(value) {
        let deductibleOwed = value - this.state.paidIn;

        this.setState({
            deductible: value,
            deductibleOwed: deductibleOwed,
            ...this.alteredState
        });
    }

    handlePaidInChange(value) {
        let deductibleOwed = this.state.deductible - value;

        this.setState({
            paidIn: value,
            deductibleOwed: deductibleOwed,
            ...this.alteredState
        });
    }

    handleDeductibleOwedChange(value) {
        let paidIn = this.state.deductible - value;

        this.setState({
            deductibleOwed: value,
            paidIn: paidIn,
            ...this.alteredState
        });
    }

    handleCoinsuranceChange(value) {
        this.setState({
            coinsurance: value,
            ...this.alteredState
        });
    }

    handleCopayChange(value) {
        this.setState({
            copay: value,
        });
    }

    handleMaxOutOfPocketChange(value) {
        let remaining = value - this.state.maxOutOfPocketPaidIn;

        this.setState({
            maxOutOfPocket: value,
            maxOutOfPocketRemaining: remaining,
            ...this.alteredState
        });
    }

    handleMaxOutOfPocketPaidInChange(value) {
        let remaining = this.state.maxOutOfPocket - value;

        this.setState({
            maxOutOfPocketPaidIn: value,
            maxOutOfPocketRemaining: remaining,
            ...this.alteredState
        });
    }

    handleMaxOutOfPocketRemainingChange(value) {
        let paidIn = this.state.deductible - value;

        this.setState({
            maxOutOfPocketRemaining: value,
            maxOutOfPocketPaidIn: paidIn,
            ...this.alteredState
        });
    }

    handleCodeUnitsChange(code, value) {
        const codes = this.state.codeList;
        const row = codes.find((item) => item.code === code);
        if (row !== undefined) {
            row.units = value;
            this.setState({
                codeList: codes,
                ...this.alteredState
            });
        }
    }

    handleCodeDelete(index) {
        const codes = this.state.codeList.filter((item, _, array) => item !== array[index] && item.parent !== array[index]);
        this.setState({
            codeList: codes,
            ...this.alteredState
        });
    }

    handleCodeAdd(codeData) {
        this.addCode(codeData, this.state.selectedInsurance, this.state.selectedLocation, this.state.selectedProvider);
    }

    handleSave() {
        this.saveData(false);
    }

    handlePrint() {
        this.saveData(true)
            .then(estimateId => {
                const root = window.location.protocol + '//' + window.location.host;
                window.open(root + '/Worksheet/Print/' + estimateId, '_blank');
            });
    }

    handleClear() {
        this.setState(this.defaultState, () => {
            this.setState(
                this.alteredState
            );
        });
    }

    isSelectedInsuranceSelfPay() {
        return this.state.selectedInsurance?.isSelfPay ?? false;
    }

    addCodesPreReqsMet() {
        return this.state.selectedProvider?.type
            && this.state.selectedInsurance?.type
            && this.state.selectedLocation?.departmentId > 0;
    }

    toast(title, message, level = 'success', autoDismiss = true) {
        this.setState({
            showNotification: true,
            notificationClass: level,
            notificationMessage: message
        });

        if (autoDismiss) {
            setTimeout(() => {
                this.setState({ showNotification: false });
            }, 2000);
        }
    }

    handleCloseNotification = () => {
        this.setState({
            showNotification: false
        });
    }

    saveData(isPrinted) {
        const data = { ...this.state };
        const apiService = new Api(this.state);
        data.estimatedCharges = apiService.calculateCharges();
        data.estimatedAltAmount = apiService.calculateAllowables();
        data.deductibleOwed = apiService.calculateRemainingDeductible();
        data.coinsuranceTotal = apiService.calculateCoinsuranceCost();
        data.copayTotal = apiService.calculateCopayCost();
        data.amountDue = apiService.calculateTotalDue();
        data.isPrinted = isPrinted;

        return Api.saveSummary(data)
            .then(resultResponse => {
                if (!isPrinted) {
                    this.toast('SAVED', ['The estimate has been saved.', <br key="_" />, `ID: ${resultResponse.estimateId}`]);
                }

                if (this.state.altered) {
                    // Reset the altered flag after a save so that we don't keep generating new IDs on print
                    // In this case, we will always use a new print id
                    this.setState({
                        id: resultResponse.estimateId,
                        altered: false
                    });

                    return resultResponse.estimateId;
                } else {
                    // We must record every print as a new estimate, but we want to display the same print id unless we have modified the document
                    const effectivePrintId = this.state.id || resultResponse.estimateId;

                    this.setState({
                        id: effectivePrintId
                    });

                    return effectivePrintId;
                }
            })
            .catch(errorResponse => {
                errorResponse.then(errorResult => {
                    this.toast('ERROR', ['An error was thrown while attempting to save the estimate to the Database:', <br />, errorResult.ExceptionMessage], 'error', false);
                });
            });
    }

    render() {
        const apiService = new Api(this.state);

        return (
            <div className="container">
                <div className="row">
                    <div className="col-sm-12 col-md-6">
                        <h1 className="page-header">Estimate Worksheet</h1>
                    </div>
                    <div className="col-sm-12 col-md-6">
                        <EstimateDatePanel id="estimateDatePanel"
                            estimateDate={this.state.estimateDate}
                            dateOfService={this.state.dateOfService}
                            onDateOfServiceChange={this.handleDateOfServiceChange} />
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <PatientInfoPanel id="patientInfoPanel"
                            patient={this.state.selectedPatient}
                            provider={this.state.selectedProvider}
                            insurance={this.state.selectedInsurance}
                            location={this.state.selectedLocation}
                            onPatientChange={this.handlePatientChange}
                            onProviderChange={this.handleProviderChange}
                            onInsuranceChange={this.handleInsuranceChange}
                            onLocationChange={this.handleLocationChange}
                            onPatientAdd={this.handlePatientAdd}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="container">
                        <div className="row">
                            <div className="col-xs-12 col-md-4 col-lg-4">
                                <div className="row">
                                    <div className="col">Benefit Information</div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-4 col-sm-12">
                                        <DeductiblePanel
                                            id="deductiblePanel"
                                            enabled={!this.isSelectedInsuranceSelfPay()}
                                            deductible={this.state.deductible}
                                            paidIn={this.state.paidIn}
                                            owed={this.state.deductibleOwed}
                                            onDeductibleChange={this.handleDeductibleChange}
                                            onPaidInChange={this.handlePaidInChange}
                                            onDeductibleOwedChange={this.handleDeductibleOwedChange}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-4 col-sm-12">
                                        <CoinsurancePanel
                                            id="coinsurancePanel"
                                            enabled={!this.isSelectedInsuranceSelfPay()}
                                            charges={apiService.calculateCharges()}
                                            coinsurance={this.state.coinsurance}
                                            onCoinsuranceChange={this.handleCoinsuranceChange}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-4 col-sm-12">
                                        <CopayPanel
                                            id="copayPanel"
                                            enabled={!this.isSelectedInsuranceSelfPay()}
                                            copay={this.state.copay}
                                            onCopayChange={this.handleCopayChange}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-4 col-sm-12">
                                        <MaxOutOfPocketPanel
                                            id="maxOutOfPocketPanel"
                                            enabled={!this.isSelectedInsuranceSelfPay()}
                                            maxOutOfPocket={this.state.maxOutOfPocket}
                                            maxOutOfPocketPaidIn={this.state.maxOutOfPocketPaidIn}
                                            maxOutOfPocketRemaining={this.state.maxOutOfPocketRemaining}
                                            onMaxOutOfPocketChange={this.handleMaxOutOfPocketChange}
                                            onMaxOutOfPocketPaidInChange={this.handleMaxOutOfPocketPaidInChange}
                                            onMaxOutOfPocketRemainingChange={this.handleMaxOutOfPocketRemainingChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-xs-12 col-lg-8">
                                <div className="row">
                                    <div className="col">The estimate is based on the following codes:</div>
                                </div>
                                <div className="row">
                                    <div className="col">
                                        <CodeTablePanel id="codeTablePanel" preReqsMet={this.addCodesPreReqsMet()} codeList={this.state.codeList}
                                            adminCodeButtons={this.state.adminCodes}
                                            maxCodes={this.maxCodes}
                                            onCodeUnitsChange={this.handleCodeUnitsChange}
                                            onCodeDelete={this.handleCodeDelete}
                                            onCodeAdd={this.handleCodeAdd} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col">
                                        <EstimateTotalsPanel id="estimateTotalsPanel"
                                            charges={apiService.calculateCharges()}
                                            allowables={apiService.calculateAllowables()}
                                            stillOwed={apiService.calculateSummaryDeductible()}
                                            coinsuranceTotal={apiService.calculateSummaryCoinsurance()}
                                            copayTotal={apiService.calculateSummaryCopay()}
                                            maxOutOfPocketRemaining={apiService.calculateMaxOutOfPocketRemaining()}
                                            outOfPocketTotal={apiService.calculateOutOfPocketAmount()}
                                            personalBalance={this.state.selectedPatient ? this.state.selectedPatient.balance : 0}
                                            badDebt={this.state.selectedPatient ? this.state.selectedPatient.badDebt : 0}
                                            amountDue={apiService.calculateTotalDue()}
                                        />
                                    </div>
                                </div>
                                <div className="row print-hidden">
                                    <div className="col btn-toolbar">
                                        <button type="button" className="btn btn-primary m-1" onClick={this.handleSave}>Save</button>
                                        <button type="button" className="btn btn-print m-1" onClick={this.handlePrint}>Print</button>
                                        <button type="button" className="btn btn-clear m-1" onClick={this.handleClear}>Clear</button>
                                    </div>
                                </div>
                                <div className="row d-none d-print-block">
                                    <div className="col">
                                        <Disclaimer />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/*    <NotificationSystem ref={this.notificationSystem} />*/}
                <NotificationGroup
                    style={{
                        right: 0,
                        bottom: 0,
                        alignItems: 'flex-start',
                        flexWrap: 'wrap-reverse'
                    }}
                >
                    <Fade>
                        {this.state.showNotification && <Notification
                            type={{ style: this.state.notificationClass, icon: true }}
                            closable={true}
                            onClose={() => this.handleCloseNotification()}
                        >
                            <span>{this.state.notificationMessage}</span>
                        </Notification>}
                    </Fade>
                </NotificationGroup>
            </div>
        )
    };
}

const useWorksheetWithParams = (props) => (
    <Worksheet
        {...props}
        params={useParams()}
    />
);

export default useWorksheetWithParams;
